import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getAllEntitesRequest,
    deleteEntiteRequest,
    createEntiteRequest,
    updateEntiteRequest,
    importEntitiesExcelRequest,
    exportEntitiesRequest,
    downloadEntitiesTemplateRequest,
} from "../../../redux/actions/entitesActions";
import Swal from "sweetalert2";
import "./EntiteManagement.css";
import ScrollToTop from "../../../Ui/UiJs/ScrollToTop";
import {
    Pencil, Trash2, Upload, Download, FileSpreadsheet,
    X, Eye, CheckCircle2, AlertTriangle, Search, RefreshCw, Plus, FileDown
} from "lucide-react";

/* ════════════════════════════════════════
   MODAL réutilisable
════════════════════════════════════════ */
const Modal = ({ open, title, onClose, size = "lg", footer, children }) => {
    if (!open) return null;
    const sizeClass = size === "xl" ? "em-modal-xl" : "";
    return (
        <div className="em-overlay" onMouseDown={onClose}>
            <div className={`em-modal ${sizeClass}`} onMouseDown={(e) => e.stopPropagation()}>
                <div className="em-modal-header">
                    <h3>{title}</h3>
                    <button className="em-modal-close" type="button" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>
                <div className="em-modal-body">{children}</div>
                {footer && <div className="em-modal-footer">{footer}</div>}
            </div>
        </div>
    );
};

/* ════════════════════════════════════════
   MODAL AJOUT / EDIT
════════════════════════════════════════ */
const EntiteModal = ({ isOpen, onClose, onSubmit, editingEntite, loading, entites }) => {
    const [formData, setFormData] = useState({ code: "", name: "", isActive: true, parentEntityId: "" });

    useEffect(() => {
        if (editingEntite) {
            setFormData({
                code: editingEntite.code || "",
                name: editingEntite.name || "",
                isActive: editingEntite.isActive !== undefined ? editingEntite.isActive : true,
                parentEntityId: editingEntite.parentEntityId || "",
            });
        } else {
            setFormData({ code: "", name: "", isActive: true, parentEntityId: "" });
        }
    }, [editingEntite, isOpen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((p) => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(formData);
    };

    if (!isOpen) return null;

    return (
        <Modal open={isOpen} title={editingEntite ? "Modifier l'entité" : "Ajouter une entité"} onClose={onClose} footer={
            <>
                <button type="button" className="em-btn em-btn-outline" onClick={onClose} disabled={loading}>Annuler</button>
                <button type="submit" className="em-btn em-btn-primary" onClick={handleSubmit} disabled={loading}>
                    {loading ? "Chargement..." : editingEntite ? "Enregistrer" : "Créer"}
                </button>
            </>
        }>
            <div className="em-form-grid">
                <div className="em-field">
                    <label>Code *</label>
                    <input name="code" value={formData.code} onChange={handleChange} required placeholder="Ex : ENT-001" />
                </div>
                <div className="em-field">
                    <label>Nom de l'entité *</label>
                    <input name="name" value={formData.name} onChange={handleChange} required placeholder="Ex : Direction Générale" />
                </div>
                <div className="em-field">
                    <label>Entité parente</label>
                    <select name="parentEntityId" value={formData.parentEntityId} onChange={handleChange}>
                        <option value="">(Aucune - Racine)</option>
                        {(entites || []).filter(e => !editingEntite || e.id !== editingEntite.id).map(e => (
                            <option key={e.id} value={e.id}>{e.name} ({e.code})</option>
                        ))}
                    </select>
                </div>
                <div className="em-field em-field-full">
                    <label className="em-check-label">
                        <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} />
                        Entité active
                    </label>
                </div>
            </div>
        </Modal>
    );
};

/* ════════════════════════════════════════
   MAIN
════════════════════════════════════════ */
const EntiteManagement = () => {
    const dispatch = useDispatch();
    const { entites, loading, bulkLoading, importResult } = useSelector(state => state.entite);

    const [search, setSearch] = useState("");
    const [filter, setFilter] = useState("all");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingEntite, setEditingEntite] = useState(null);

    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkFile, setBulkFile] = useState(null);
    const [bulkDryRun, setBulkDryRun] = useState(true);
    const [bulkAction, setBulkAction] = useState("add");

    const refreshData = () => dispatch(getAllEntitesRequest());
    useEffect(() => { refreshData(); }, [dispatch]);

    const openCreate = () => { setEditingEntite(null); setIsModalOpen(true); };
    const openEdit = (e) => { setEditingEntite(e); setIsModalOpen(true); };
    const closeModal = () => { setIsModalOpen(false); setEditingEntite(null); };

    const handleSubmit = (formData) => {
        const cleanData = { ...formData, parentEntityId: formData.parentEntityId ? parseInt(formData.parentEntityId, 10) : null };
        if (editingEntite) dispatch(updateEntiteRequest(editingEntite.id, cleanData));
        else dispatch(createEntiteRequest(cleanData));
        closeModal();
    };

    const handleDelete = (id, name) => {
        Swal.fire({
            title: "Suppression", text: `Supprimer "${name}" ?`, icon: "warning",
            showCancelButton: true, confirmButtonColor: "#2a5ca9", confirmButtonText: "Oui, supprimer"
        }).then((res) => { if (res.isConfirmed) dispatch(deleteEntiteRequest(id)); });
    };

    const runBulk = () => {
        if (!bulkFile) { Swal.fire({ icon: "warning", text: "Veuillez sélectionner un fichier Excel.", confirmButtonColor: '#2a5ca9' }); return; }
        dispatch(importEntitiesExcelRequest(bulkFile, bulkDryRun, bulkAction));
        if (!bulkDryRun) setBulkOpen(false);
    };

    const resolveHierarchy = (entity) => {
        const path = [];
        let curr = entity;
        while (curr) {
            path.unshift(curr);
            curr = (entites || []).find(e => e.id === curr.parentEntityId);
            if (path.length > 10) break;
        }
        return {
            dept: path[0]?.name || "—",
            service: path[1]?.name || "—",
            section: path[2]?.name || "—"
        };
    };

    const filteredEntites = useMemo(() => {
        return (entites || []).filter((e) => {
            const matchSearch = (e.name || "").toLowerCase().includes(search.toLowerCase()) || (e.code || "").toLowerCase().includes(search.toLowerCase());
            const matchFilter = filter === "all" ? true : filter === "active" ? e.isActive : !e.isActive;
            return matchSearch && matchFilter;
        });
    }, [entites, search, filter]);

    const importErrors = importResult?.errors || importResult?.errorList || [];
    const successCount = importResult?.createdCount || importResult?.successCount || 0;

    return (
        <div className="em-page">
            <div className="em-header">
                <div>
                    <h2>Gestion des entités</h2>
                    <p className="em-subtitle">Structure organisationnelle : Département &gt; Service &gt; Section</p>
                </div>
                <div className="em-header-actions">
                    <button className="em-btn em-btn-outline" onClick={() => dispatch(downloadEntitiesTemplateRequest())} disabled={bulkLoading}>
                        <Download size={14} /> Template
                    </button>
                    <button className="em-btn em-btn-outline" onClick={() => dispatch(exportEntitiesRequest())} disabled={bulkLoading}>
                        <FileDown size={14} /> Exporter
                    </button>
                    <button className="em-btn em-btn-primary" onClick={() => setBulkOpen(true)}>
                        <Upload size={14} /> Import massif
                    </button>
                    <button className="em-btn em-btn-primary" onClick={openCreate}>
                        <Plus size={14} /> Nouvelle Entité
                    </button>
                </div>
            </div>

            <div className="em-toolbar">
                <div className="em-search-wrapper">
                    <Search className="em-search-icon" size={14} />
                    <input className="em-search" placeholder="Rechercher une entité..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </div>
                <div className="em-filter-tabs">
                    {[{ k: "all", l: "Tous" }, { k: "active", l: "Actifs" }, { k: "inactive", l: "Inactifs" }].map((f) => (
                        <button key={f.k} className={`em-filter-tab ${filter === f.k ? 'active' : ''}`} onClick={() => setFilter(f.k)}>{f.l}</button>
                    ))}
                </div>
                <div className="em-count">{filteredEntites.length} entité{filteredEntites.length > 1 ? "s" : ""}</div>
            </div>

            <div className="em-table-wrapper">
                {loading ? (
                    <div className="em-loading"><div className="em-spinner" /><span>Chargement...</span></div>
                ) : (
                    <table className="em-table">
                        <thead>
                            <tr>
                                <th>Code</th>
                                <th>Département</th>
                                <th>Service</th>
                                <th>Section</th>
                                <th>Statut</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredEntites.map((e) => {
                                const h = resolveHierarchy(e);
                                return (
                                    <tr key={e.id}>
                                        <td><span className="em-code">{e.code}</span></td>
                                        <td className="em-name">{h.dept}</td>
                                        <td className="em-muted">{h.service}</td>
                                        <td className="em-muted">{h.section}</td>
                                        <td>
                                    <span className={`jm-badge ${e.isActive ? 'jm-badge-green' : 'jm-badge-grey'}`}>
                                        {e.isActive ? "Actif" : "Inactif"}
                                    </span>
                                        </td>
                                        <td className="text-end">
                                            <div className="d-flex justify-content-end gap-2">
                                                <button className="em-icon-btn" onClick={() => openEdit(e)}><Pencil size={14} /></button>
                                                <button className="em-icon-btn em-icon-btn-danger" onClick={() => handleDelete(e.id, e.name)}><Trash2 size={14} /></button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* MODALS */}
            <EntiteModal isOpen={isModalOpen} onClose={closeModal} onSubmit={handleSubmit} editingEntite={editingEntite} loading={loading} entites={entites} />

            <Modal open={bulkOpen} title="Ajout massif (Excel)" onClose={() => setBulkOpen(false)} footer={
                <button className="em-btn em-btn-primary" onClick={runBulk} disabled={bulkLoading}>Exécuter</button>
            }>
                <div className="em-form-section">
                    <div className="jm-form-grid">
                        <div className="jm-field">
                            <label>Action</label>
                            <select value={bulkAction} onChange={(e) => setBulkAction(e.target.value)}>
                                <option value="add">Ajouter</option>
                                <option value="edit">Modifier</option>
                            </select>
                        </div>
                        <div className="jm-field">
                            <label className="jm-check-label">
                                <input type="checkbox" checked={bulkDryRun} onChange={(e) => setBulkDryRun(e.target.checked)} />
                                Prévisualisation avant l'import
                            </label>
                        </div>
                        <div className="jm-field jm-field-full">
                            <label>Fichier</label>
                            <input type="file" accept=".xlsx,.xls" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} />
                        </div>
                    </div>

                    {bulkLoading && (
                        <div className="em-loading" style={{ justifyContent: "flex-start", padding: "10px 0 0" }}>
                            <div className="em-spinner" />
                            <span>Traitement en cours...</span>
                        </div>
                    )}

                    {importResult && bulkDryRun && !bulkLoading && (
                        <div style={{ marginTop: 16 }}>
                             {importErrors.length > 0 ? (
                                <div className="em-alert em-alert-danger">
                                    <AlertTriangle size={16} />
                                    <span>{importErrors.length} erreurs détectées dans le fichier.</span>
                                </div>
                             ) : (
                                <div className="em-alert em-alert-success">
                                    <CheckCircle2 size={16} />
                                    <span>Le fichier est valide ! ({successCount} lignes prêtes)</span>
                                </div>
                             )}
                        </div>
                    )}
                </div>
            </Modal>
            <ScrollToTop />
        </div>
    );
};

export default EntiteManagement;